import { george, observer, pr, React, RouteComponentProps, theme, ui } from "aaa";
import { InfoBox } from "components/common/InfoBox";
import { OptioPayRoutes } from "components/optiopay/OptioPayRoutes";
import { A11yRefocus } from "components/util/primitives";
import { TopBackgroundColor } from "components/util/TopBackgroundColor";
import * as _ from "lodash";
import authStore from "stores/AuthStore";
import externalInterfaceStore from "stores/ExternalInterfaceStore";
import trackingStore from "stores/TrackingStore";
import { IHierarchyNode } from "types/store";
import { ProductThumbnailBig, STORE_BORDER_RADIUS } from "./ProductThumbnailBig";
import { ProductThumbnailSmall } from "./ProductThumbnailSmall";
import { getExternalRedirect, IStorePlugin, IStoreProduct, replaceAdditionalParams, StoreStore } from "./StoreStore";

type IProps = RouteComponentProps<{}> & {
    store: StoreStore
};

@observer
export class StoreLandingPage extends React.Component<IProps, {}> {
    firstStoreImageRef = React.createRef<HTMLAnchorElement>();

    async componentDidMount() {
        window.scrollTo(0, 0);
    }

    componentDidUpdate() {
        // focus first store image in order to be able to use the store in the native app
        if (this.firstStoreImageRef.current) {
            this.firstStoreImageRef.current.focus();
        }

        if (!this.props.store.isLoading && externalInterfaceStore.category) {
            const categoryPosition = document.getElementById(externalInterfaceStore.category)?.getBoundingClientRect();
            if (categoryPosition) {
                window.scrollTo(0, categoryPosition.top - 10);
            }
        }
    }

    getHeadline() {
        return this.props.store.storeConfiguration?.generalInformation.type === "ACTIVE"
            && this.props.store.storeConfiguration.generalInformation.customHeadLine
            ? this.props.store.storeConfiguration.generalInformation.customHeadLine
            : undefined;
    }

    renderExtras() {
        if (this.props.store.activePlugins.length === 0) {
            return null;
        }

        // Currently Hybrid only supports rendering the optio pay plugin
        const optioPayPlugin = this.props.store.activePlugins.find(p => p.productCode === "PI-VOUCHER");
        if (!optioPayPlugin) {
            return null;
        }

        return (
            <>
                <h2
                    id={ui.__("store.yourExtras")}
                    className="theme-title-color"
                    style={{ fontWeight: "bold", marginTop: theme.MARGIN_SMALL, marginLeft: theme.MARGIN, marginBottom: 5, fontSize: 20 }}
                >
                    {ui.__("store.yourExtras")}
                </h2>

                <div
                    style={{
                        display: "flex",
                        padding: theme.MARGIN,
                        paddingTop: theme.MARGIN_SMALL
                    }}
                >
                    <a
                        target="_blank"
                        href={getExternalRedirect(replaceAdditionalParams(`${OptioPayRoutes.ROOT}?ADDITIONAL_PARAMS&access_token=`, { productCode: optioPayPlugin.productCode }))}
                        onClick={this.handleClickThumbnail(optioPayPlugin, "yourExtrasClicked")}
                        role="button"
                        aria-label={`
                            ${ui.__("optiopay.document.title")}.
                            ${ui.__("optiopay.vouchers.totalBoost", { totalBoost: this.props.store.vouchersStore.totalSavingsBoost })}.
                            ${ui.__("store.yourExtras.optiopay.voucherCount", { count: this.props.store.vouchersStore.vouchers.length })}
                        `}
                        className="theme-paper-color"
                        style={{
                            textDecoration: "none",
                            borderRadius: STORE_BORDER_RADIUS,
                            width: "100%",
                            maxWidth: 500,
                            paddingLeft: 16,
                            paddingRight: 16,
                            paddingTop: theme.MARGIN,
                            paddingBottom: theme.MARGIN,
                            boxShadow: "0px 0px 10px rgba(0,0,0,0.10)",
                            overflow: "hidden",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center"
                        }}
                    >
                        <pr.LogoFinMagic style={{ width: 64, height: 64 }} />
                        <div style={{ flexGrow: 1, marginLeft: theme.MARGIN_SMALL }}>
                            <pr.P_16_TITLE style={{ fontWeight: 600, marginBottom: theme.MARGIN_SMALL }} role="text">{ui.__("optiopay.document.title")}</pr.P_16_TITLE>
                            <pr.P_14_CONTENT role="text">
                                {ui.__(
                                    "optiopay.vouchers.totalBoost",
                                    { totalBoost: this.props.store.vouchersStore.totalSavingsBoost }
                                )}
                            </pr.P_14_CONTENT>
                        </div>
                        <div className="optiopay-badge" style={{ borderRadius: 10, fontSize: 12, padding: 2, paddingLeft: 6, paddingRight: 6 }}>
                            {this.props.store.vouchersStore.vouchers.length}
                        </div>
                        <pr.IconChevronRight style={{ color: "#A7B6C7" }} />
                    </a>
                </div>
            </>
        );
    }

    handleClickThumbnail = (item: IStoreProduct | IStorePlugin, action: string = "productBannerClicked") => async () => {
        await trackingStore.trackEvent({
            eventName: action,
            productKey: item.type === "PRODUCT" ? item.channelProduct : item.productCode
        });
    }

    render() {
        let numBig = 1;
        // if (!helpers.isIPhone()) {
        //     if (ui.isTabletPortrait) {
        //         numBig = 2;
        //     } else if (ui.isTabletLandscape) {
        //         numBig = 3;
        //     }
        // }

        // If not scrolling, then small thumbs are same width as big thumb
        const numSmall = this.props.store.items.length - numBig;
        const scrolling = true; // numSmall > numBig;
        const thumbWidth = scrolling ? undefined : (ui.windowWidth - (numBig + 1) * theme.MARGIN) / numBig;

        const headline = this.props.store.storeConfiguration && this.getHeadline();

        const CategoryArea = ({ children }: { children: React.ReactNode }) => (
            <div
                style={{
                    display: "flex",
                    overflowX: "auto",
                    paddingLeft: theme.MARGIN,
                    paddingTop: theme.MARGIN_SMALL
                }}
            >
                {children}
            </div>
        );

        const recommendedItemCount = 3;
        const extraItems = this.props.store.items.filter(item => item.type === "PLUGIN" && item.productCode === "PI-VOUCHER");
        const productItems = this.props.store.items.filter(item => item.type === "PRODUCT");
        const recommendedItems = _.take(productItems, recommendedItemCount);
        const mostRecommendedItems = _.take(recommendedItems, numBig);
        const otherRecommendedItems = _.takeRight(recommendedItems, recommendedItemCount - numBig);

        return (
            <george.Background>
                <TopBackgroundColor />

                <div
                    className="theme-header-background-color"
                    style={{
                        padding: theme.MARGIN,
                        height: headline ? undefined : 122,
                        boxSizing: "border-box",
                        position: "absolute",
                        top: externalInterfaceStore.isGeorgeWeb ? theme.WEB_HEADER_HEIGHT : 0,
                        left: 0,
                        right: 0
                    }}
                >
                    <pr.P_28_TITLE style={{ color: "white", fontWeight: "bold" }}>
                        {ui.__("store.landingPage.title")}
                    </pr.P_28_TITLE>
                </div>

                {headline &&
                    <A11yRefocus>
                        <InfoBox
                            content={
                                <pr.P_16_CONTENT>{headline}</pr.P_16_CONTENT>}
                            style={{ marginBottom: theme.MARGIN, width: "unset", marginLeft: theme.MARGIN, marginRight: theme.MARGIN, marginTop: 74 + theme.MARGIN_SMALL, }}
                        />
                    </A11yRefocus>
                }

                <div
                    style={{
                        position: "relative",
                        paddingTop: !headline ? 74 : 0,
                        paddingBottom: 50
                        // Cannot use global padding left/right to not cut off horizontal scroll area
                    }}
                >
                    <div style={{ paddingLeft: theme.MARGIN, paddingRight: theme.MARGIN, paddingBottom: theme.MARGIN, display: "flex", justifyContent: "center" }}>
                        {mostRecommendedItems.map((item, index) => (
                            <ProductThumbnailBig
                                key={index}
                                ref={index === 0 ? this.firstStoreImageRef : undefined}
                                isLoading={this.props.store.isLoading}
                                item={item}
                                width={thumbWidth}
                                onClick={this.props.store.isLoading ? undefined : this.handleClickThumbnail(item)}
                                marginRight={index < numBig - 1 ? theme.MARGIN : 0}
                            />
                        ))}
                    </div>

                    {this.renderExtras()}

                    {otherRecommendedItems.length > 0 &&
                        <h2
                            id={ui.__("store.recommended")}
                            className="theme-title-color"
                            style={{ fontWeight: "bold", marginTop: theme.MARGIN_SMALL, marginLeft: theme.MARGIN, marginBottom: 5, fontSize: 20 }}
                        >
                            {ui.__("store.recommended")}
                        </h2>
                    }

                    <CategoryArea>
                        {_.chunk(otherRecommendedItems, 2).map((column, columnIndex) => (
                            <div key={columnIndex}>
                                {column.map((item, index) => (
                                    <ProductThumbnailSmall
                                        key={`${columnIndex}_${index}`}
                                        isLoading={this.props.store.isLoading}
                                        item={item}
                                        marginRight={theme.MARGIN}
                                        marginBottom={theme.MARGIN}
                                        width={thumbWidth}
                                        onClick={this.props.store.isLoading ? undefined : this.handleClickThumbnail(item)}
                                    />
                                ))}
                            </div>
                        ))}
                    </CategoryArea>

                    {["productNodes", "pluginNodes"].map(nodeType => {
                        const productCategories = (this.props.store.categories?.[nodeType] ?? []) as IHierarchyNode[];

                        return productCategories.map(productCategory => {
                            const categoryProducts = this.props.store.items.filter(item => {

                                if (item.type === "PRODUCT") {
                                    return item.offer && productCategory.extIds.includes(item.offer.channelProduct);
                                } else if (item.type === "PLUGIN") {
                                    return productCategory.extIds.includes(item.productCode);
                                }

                                return false;
                            });

                            const categoryName = productCategory.names.find(categoryNameItem => categoryNameItem.language === authStore.locale.toUpperCase())?.name;

                            return nodeType === "productNodes" &&
                                productCategory.extIds.length > 0 &&
                                (categoryProducts.length > 0 || (this.props.store.isLoading && this.props.store.items.length > 0))
                                ? (
                                    <React.Fragment key={categoryName}>
                                        <h2
                                            id={categoryName}
                                            className="theme-title-color"
                                            style={{ marginTop: theme.MARGIN_SMALL, marginLeft: theme.MARGIN, marginBottom: 5, fontWeight: 600, fontSize: 20 }}
                                        >
                                            {categoryName}
                                        </h2>
                                        <CategoryArea>
                                            {_.chunk(this.props.store.isLoading ? new Array(productCategory.extIds.length).fill({ type: "PRODUCT" }) : categoryProducts, 2)
                                                .map((column, columnIndex) => (
                                                    <div key={columnIndex}>
                                                        {column.map((product, index) => (
                                                            <ProductThumbnailSmall
                                                                key={`${columnIndex}_${index}`}
                                                                isLoading={this.props.store.isLoading}
                                                                item={product}
                                                                marginRight={theme.MARGIN}
                                                                marginBottom={theme.MARGIN}
                                                                width={thumbWidth}
                                                                onClick={this.props.store.isLoading ? undefined : this.handleClickThumbnail(product)}
                                                            />
                                                        ))}
                                                    </div>
                                                ))}
                                        </CategoryArea>
                                    </React.Fragment>
                                )
                                : null;
                        });
                    })}
                    {extraItems.length > 0 &&
                        <>
                            <h2
                                className="theme-title-color"
                                style={{ fontWeight: "bold", marginTop: theme.MARGIN_SMALL, marginLeft: theme.MARGIN, marginBottom: 5, fontSize: 20 }}
                            >
                                {ui.__("store.extras")}
                            </h2>
                            <CategoryArea>
                                {_.chunk(extraItems, 2).map((column, columnIndex) => (
                                    <div key={columnIndex}>
                                        {column.map((item, index) => (
                                            <ProductThumbnailSmall
                                                key={`${columnIndex}_${index}`}
                                                isLoading={this.props.store.isLoading}
                                                item={item}
                                                marginRight={theme.MARGIN}
                                                marginBottom={theme.MARGIN}
                                                width={thumbWidth}
                                                onClick={this.props.store.isLoading ? undefined : this.handleClickThumbnail(item)
                                                }
                                            />
                                        ))}
                                    </div>
                                ))}
                            </CategoryArea>
                        </>
                    }
                </div>
            </george.Background >
        );
    }
}




// WEBPACK FOOTER //
// ./src/components/store/StoreLandingPage.tsx